import React, { useState, useContext } from "react";
import { PhotoManagerContext } from "..";
import { EventType } from "../types";
import toastr from "../../../common/toastr";
import {
  validateName,
  restrictedTermsError
} from "../../../../utils/validateName";
import { createGalleryAPI } from "../../API/api";
import {
  decodeEntityPath,
  getEncodedEntityPath,
  getEntityInfoFromPath,
  getEntitySegment
} from "../FolderFeatures/EncryptionOfFolder";

interface CreateEventModalProps {
  onClose: () => void;
  onSuccess: (event: EventType, folderId: number | null) => void;
  folderId: number | null;
}

const CreateEventModal: React.FC<CreateEventModalProps> = ({
  onClose,
  onSuccess,
  folderId
}) => {
  const [name, setName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const context = useContext(PhotoManagerContext);

  const handleCreate = async () => {
    const isValidName = validateName(name);
    if (!isValidName) {
      toastr.error(restrictedTermsError);
      return;
    } else if (!name.trim()) {
      toastr.error("Please enter a valid name");
      return;
    }

    setLoading(true);
    try {
      const encodedPath = folderId ? context?.encodedString : null;
      const decodedPath = encodedPath ? decodeEntityPath(encodedPath) : null;

      const lastEntity = decodedPath
        ? getEntityInfoFromPath(decodedPath)
        : null;

      const parentFolderId =
        lastEntity?.type === "folder" || lastEntity?.type === "reference"
          ? lastEntity.id
          : null;

      const createdEvent = await createGalleryAPI(
        name,
        folderId || parentFolderId
      );
      toastr.success("Event created successfully");

      const newSegment = getEntitySegment({
        id: createdEvent.id,
        referenceId: null,
        type: "Gallery",
        isReference: false
      });

      const updatedDecodedPath = decodedPath
        ? `${decodedPath}/${newSegment}`
        : newSegment;

      const newEncodedPath = getEncodedEntityPath(updatedDecodedPath);
      context?.setEncodedString(newEncodedPath);

      const url = new URL(window.location.href);
      url.searchParams.set("p", newEncodedPath);
      window.location.href = url.toString();
    } catch (error) {
      console.error(`Error creating event:`, error);
      toastr.error("Failed to create event");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleCreate();
    }
  };

  return (
    <div>
      <div id="filter-dialog" className="modal fade in geo-modal" role="dialog">
        <div className="modal-dialog filter-dialog-content ws-create-modal">
          <div className="geo-modal__header">
            <h4>Create an Event</h4>
          </div>
          <button
            type="button"
            className="close"
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
          <div className="modal-content">
            <p>Create albums inside the event to upload photos</p>
            <p className="ws-small-text">
              You can enter any other details in the edit page
            </p>
            <div className="row geo geo-form geo-form--fw">
              <input
                id="name-input"
                type="text"
                className="geo-form__input ws-geo-input"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Enter event name`}
                autoFocus
                disabled={loading}
              />
            </div>
            <div className="modal-footer geo-modal__actions dont-break ws-delete-footer-btns">
              <button
                className="geo-btn geo-btn--secondary geo-btn--caps"
                style={{ padding: "12px 30px", marginLeft: "15px" }}
                onClick={handleCreate}
                disabled={loading || !name.trim()}
              >
                {loading ? (
                  <div className="loading-spinner">
                    <span className="fa fa-spinner fa-spin"></span>
                  </div>
                ) : (
                  `CREATE EVENT`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade in" />
    </div>
  );
};

export default CreateEventModal;
